import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../../billMemory/api';
import { shortDate, makeMatchName, HOW_LEARNED } from '../../billMemory/core';

/**
 * Learned names — the main table (source spec, "Settings: the Bill Memory page"). Exactly the
 * source's columns; a row click opens its change log below it; Owner and Manager get the row and
 * page actions; Forget leaves an Undo on the row for 10 seconds instead of a confirmation popup.
 */
export const HOW = HOW_LEARNED; // shared with the server's export (core/reasons.js)
export const STATUS = { trusted: 'Trusted', learning: 'Learning', confusing: 'Confusing', not_stock: 'Not stock', paused: 'Paused' };
const COLUMNS = ['Name as written', 'Means item', 'Vendor', 'How it learned', 'Confirmations', 'Status', 'Last used', 'Taught by'];
const UNDO_MS = 10000;
const link = { background: 'none', border: 'none', padding: 0, marginRight: 10, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', font: 'inherit' };

const means = (r) => {
  if (r.item) return `${r.item.name}${r.item.pack ? ` (${r.item.pack})` : ''}`;
  if (r.status === 'not_stock') return 'Not stock';
  return (r.votes || []).map((v) => `${v.name} ${v.n} votes`).join(', ');
};

function ItemPicker({ items, onPick, onCancel }) {
  const [q, setQ] = useState('');
  const hits = useMemo(() => {
    const m = makeMatchName(q);
    return m ? (items || []).filter((it) => makeMatchName(it.name || it.item_name || '').includes(m)).slice(0, 8) : [];
  }, [q, items]);
  return (
    <div className="bm-picker">
      <input className="bm-input" autoFocus value={q} placeholder="Search your items" onChange={(e) => setQ(e.target.value)} />
      <div className="suggestions-row">
        {hits.map((it) => (
          <button key={it.item_id} type="button" className="suggestion-chip" onClick={() => onPick(it)}>{it.name || it.item_name}</button>
        ))}
        <button type="button" className="suggestion-chip" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function LearnedNamesTab({ canEdit, businessId }) {
  const [data, setData] = useState({ rows: [], counts: { total: 0, trusted: 0, learning: 0, confusing: 0, not_stock: 0, paused: 0 } });
  const [filters, setFilters] = useState({ search: '', status: '', vendor: '', how: '', taughtBy: '' });
  const [open, setOpen] = useState(null);
  const [log, setLog] = useState(null);
  const [picking, setPicking] = useState(null); // { id, action } | { add: true }
  const [items, setItems] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [undos, setUndos] = useState({}); // id → { token, name }
  const [adding, setAdding] = useState(null);
  const [byUser, setByUser] = useState(null);
  const [error, setError] = useState('');
  const timers = useRef({});

  const load = useCallback(async (f = filters) => {
    try {
      const r = await api.getLessons(f);
      setData({ rows: r.rows || [], counts: r.counts || {} });
    } catch (e) { setError('Could not load the learned names.'); }
  }, [filters]);

  useEffect(() => { load(filters); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.getVendors().then((r) => setVendors((r.rows || []).map((v) => v.vendor))).catch(() => {});
    const pending = timers.current; // the same object the Forget timers are added to
    return () => Object.values(pending).forEach(clearTimeout);
  }, []);

  const needItems = async () => {
    if (!items) setItems(await api.getInventory(businessId).catch(() => []));
  };
  const act = async (fn) => {
    setError('');
    try { await fn(); await load(); } catch (e) { setError((e && e.message) || 'That did not work.'); }
  };
  const openRow = async (id) => {
    if (open === id) { setOpen(null); return; }
    setOpen(id);
    const r = await api.getActivity().catch(() => ({ rows: [] }));
    setLog((r.rows || []).filter((x) => x.lesson_id === id));
  };
  const doForget = async (r) => {
    setError('');
    try {
      const out = await api.forget(r.id);
      setData((d) => ({ ...d, rows: d.rows.filter((x) => x.id !== r.id) }));
      setUndos((u) => ({ ...u, [r.id]: { token: out.undo_token, name: r.name_as_written, at: data.rows.findIndex((x) => x.id === r.id) } }));
      timers.current[r.id] = setTimeout(() => setUndos((u) => { const n = { ...u }; delete n[r.id]; return n; }), UNDO_MS);
    } catch (e) { setError('Could not forget that name.'); }
  };
  const doUndo = async (id) => {
    const u = undos[id];
    clearTimeout(timers.current[id]);
    setUndos((x) => { const n = { ...x }; delete n[id]; return n; });
    if (u) await act(() => api.undo(u.token));
  };

  const taughtByUsers = useMemo(() => {
    const m = new Map();
    data.rows.forEach((r) => { if (r.taught_by_id) m.set(r.taught_by_id, r.taught_by || `User ${r.taught_by_id}`); });
    return [...m];
  }, [data.rows]);
  const c = data.counts || {};
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="bm-tab">
      <div className="bm-filters">
        <input className="bm-input" type="search" placeholder="Search name or item" value={filters.search} onChange={set('search')} />
        <select className="bm-select" value={filters.status} onChange={set('status')} aria-label="Status">
          <option value="">Status: all</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="bm-select" value={filters.vendor} onChange={set('vendor')} aria-label="Vendor">
          <option value="">Vendor: all</option>
          <option value="any">Any vendor</option>
          {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
        <select className="bm-select" value={filters.how} onChange={set('how')} aria-label="How it learned">
          <option value="">How it learned: all</option>
          {Object.entries(HOW).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="bm-select" value={filters.taughtBy} onChange={set('taughtBy')} aria-label="Taught by">
          <option value="">Taught by: all</option>
          {taughtByUsers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      </div>

      <div className="bm-count">
        {`${c.total || 0} learned names: ${c.trusted || 0} Trusted, ${c.learning || 0} Learning, ${c.confusing || 0} Confusing, ${c.not_stock || 0} Not stock`}
      </div>

      {canEdit && (
        <div className="bm-page-actions">
          <button type="button" className="irr-secondary-btn" onClick={async () => { await needItems(); setAdding({ name: '', scope: 'shop', vendor_id: '' }); }}>Add a name</button>
          <button type="button" className="irr-secondary-btn" onClick={() => setByUser({ user: '', preview: null })}>Forget everything taught by one user</button>
          <button type="button" className="irr-secondary-btn" onClick={() => api.downloadExport().catch(() => setError('Export failed.'))}>Export</button>
        </div>
      )}

      {adding && (
        <div className="bm-panel">
          <input className="bm-input" placeholder="Name as written on the bill" value={adding.name} onChange={(e) => setAdding({ ...adding, name: e.target.value })} />
          <select className="bm-select" value={adding.scope === 'vendor' ? adding.vendor_id : ''} onChange={(e) => setAdding({ ...adding, scope: e.target.value ? 'vendor' : 'shop', vendor_id: e.target.value })} aria-label="Scope">
            <option value="">Any vendor</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <ItemPicker items={items} onCancel={() => setAdding(null)} onPick={(it) => act(async () => {
            await api.addLesson({ name: adding.name, item_id: it.item_id, scope: adding.scope, vendor_id: adding.vendor_id || undefined });
            setAdding(null);
          })} />
        </div>
      )}

      {byUser && (
        <div className="bm-panel">
          <select className="bm-select" value={byUser.user} onChange={async (e) => {
            const user = e.target.value;
            const p = user ? await api.forgetByUser(Number(user), true).catch(() => ({ rows: [] })) : { rows: [] };
            setByUser({ user, preview: p.rows || [] });
          }} aria-label="User">
            <option value="">Pick the user</option>
            {taughtByUsers.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          {byUser.preview && (
            <div className="bm-note">
              {byUser.preview.length} names: {byUser.preview.map((r) => r.name_as_written).join(', ')}
            </div>
          )}
          {byUser.preview && byUser.preview.length > 0 && (
            <button type="button" className="irr-primary-btn" onClick={async () => {
              const out = await api.forgetByUser(Number(byUser.user), false);
              setByUser(null);
              await load();
              setUndos((u) => ({ ...u, user: { token: out.undo_token, name: `${out.forgotten} names` } }));
              timers.current.user = setTimeout(() => setUndos((u) => { const n = { ...u }; delete n.user; return n; }), UNDO_MS);
            }}>Forget these names</button>
          )}
          <button type="button" className="irr-secondary-btn" onClick={() => setByUser(null)}>Cancel</button>
        </div>
      )}

      {undos.user && (
        <div className="bm-note">Forgot {undos.user.name}. <button type="button" style={link} onClick={() => doUndo('user')}>Undo</button></div>
      )}
      {error && <div className="bm-note bm-error">{error}</div>}

      <table className="irr-qty-table bm-table">
        <thead>
          <tr>{COLUMNS.map((h) => <th key={h}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {Object.entries(undos).filter(([id]) => id !== 'user').map(([id]) => (
            <tr key={`undo-${id}`}><td colSpan={COLUMNS.length}>Forgotten. <button type="button" style={link} onClick={() => doUndo(id)}>Undo</button></td></tr>
          ))}
          {data.rows.map((r) => (
            <React.Fragment key={r.id}>
              <tr className="bm-row" onClick={() => openRow(r.id)}>
                <td>
                  <div>{r.name_as_written}</div>
                  {canEdit && (
                    <div className="bm-row-actions" onClick={(e) => e.stopPropagation()}>
                      {r.status !== 'confusing' && <button type="button" style={link} onClick={async () => { await needItems(); setPicking({ id: r.id, action: 'change_item' }); }}>Change item</button>}
                      {r.status === 'confusing' && <button type="button" style={link} onClick={async () => { await needItems(); setPicking({ id: r.id, action: 'make_fixed' }); }}>Make it fixed</button>}
                      <button type="button" style={link} onClick={() => setPicking({ id: r.id, action: 'change_scope' })}>Change scope</button>
                      {r.status !== 'confusing' && r.status !== 'not_stock' && <button type="button" style={link} onClick={() => act(() => api.patchLesson(r.id, { action: 'always_ask' }))}>Always ask</button>}
                      {r.status === 'paused'
                        ? <button type="button" style={link} onClick={() => act(() => api.patchLesson(r.id, { action: 'resume' }))}>Resume</button>
                        : <button type="button" style={link} onClick={() => act(() => api.patchLesson(r.id, { action: 'pause' }))}>Pause</button>}
                      <button type="button" style={link} onClick={() => doForget(r)}>Forget</button>
                    </div>
                  )}
                </td>
                <td>{means(r)}</td>
                <td>{r.vendor ? r.vendor.name : 'Any vendor'}</td>
                <td>{HOW[r.how] || r.how}</td>
                <td>{r.status === 'confusing' ? '—' : r.confirmations}</td>
                <td>{STATUS[r.status] || r.status}</td>
                <td>{shortDate(r.last_used)}</td>
                <td>{r.taught_by || (r.status === 'confusing' ? 'Several' : '—')}</td>
              </tr>
              {picking && picking.id === r.id && (
                <tr><td colSpan={COLUMNS.length} onClick={(e) => e.stopPropagation()}>
                  {picking.action === 'change_scope' ? (
                    <select className="bm-select" defaultValue="" aria-label="New scope" onChange={(e) => {
                      const v = e.target.value;
                      if (!v) return;
                      setPicking(null);
                      act(() => api.patchLesson(r.id, v === 'shop' ? { action: 'change_scope', scope: 'shop' } : { action: 'change_scope', scope: 'vendor', vendor_id: Number(v) }));
                    }}>
                      <option value="">Move to…</option>
                      <option value="shop">Any vendor</option>
                      {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  ) : (
                    <ItemPicker items={items} onCancel={() => setPicking(null)} onPick={(it) => { setPicking(null); act(() => api.patchLesson(r.id, { action: picking.action, item_id: it.item_id })); }} />
                  )}
                </td></tr>
              )}
              {open === r.id && (
                <tr className="bm-details"><td colSpan={COLUMNS.length}>
                  {(log || []).length === 0 ? <div className="bm-note">No changes recorded yet.</div> : (
                    <ul className="bm-log">
                      {log.map((g) => <li key={g.id}>{`${shortDate(g.at)}: ${String(g.action).replace(/^manual_/, '').replace(/_/g, ' ')}${g.user ? ` by ${g.user}` : ''}${g.stock_in_id ? `, stock-in #${g.stock_in_id}` : ''}${g.after && g.after.confirmations != null ? `. ${g.after.confirmations} confirmations` : ''}.`}</li>)}
                    </ul>
                  )}
                </td></tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
